/**
 * Sessions API
 *
 * Wraps `/api/v1/sessions` and `/api/v1/session/{id}` endpoints.
 * The shared axios client has baseURL `/api/v1`, so paths here are
 * relative to that prefix.
 */

import client from './client';
import { config } from '../config/env';

export async function listSessions() {
  return client.get('/sessions');
}

export async function createSession(payload) {
  return client.post('/sessions/new', payload);
}

export async function getSession(sessionId) {
  return client.get(`/session/${sessionId}`);
}

export async function updateSession(sessionId, body) {
  return client.patch(`/session/${sessionId}`, body);
}

export async function deleteSession(sessionId) {
  return client.delete(`/session/${sessionId}`);
}

/**
 * Create a new session with structured options. Mirrors
 * AthenaBackendService.createNewSession(tenantId, analysisName, options).
 *
 * Note: the legacy method's `tenantId` arg is ignored by the server (tenant
 * scoping comes from the JWT) — preserved here for signature parity.
 */
export async function createNewSession(tenantId, analysisName, options = {}) {
  const {
    taraLevel = 'sub_component',
    analysisMode = 'individual',
    parentSessions = [],
  } = options;
  return client.post('/sessions/new', {
    analysis_name: analysisName,
    tara_level: taraLevel,
    analysis_mode: analysisMode,
    parent_sessions: parentSessions,
  });
}

/**
 * List sessions available for hierarchical linking at a given target level.
 * Mirrors AthenaBackendService.getSessionsForLinking(targetLevel).
 */
export async function getSessionsForLinking(targetLevel) {
  return client.get(
    `/sessions/available-for-linking?target_level=${encodeURIComponent(targetLevel)}`
  );
}

/**
 * Fetch a single session whose URL embeds the tenant ID in the path.
 * Mirrors AthenaBackendService.getSession(tenantId, sessionId).
 *
 * Disambiguated from `getSession(sessionId)` — that variant uses the
 * `/session/{id}` URL shape. Both exist because the backend exposes both.
 */
export async function getSessionForTenant(tenantId, sessionId) {
  return client.get(`/${tenantId}/session/${sessionId}`);
}

/**
 * Save the manual item-definition markdown for a session.
 *
 * Preserves the original cross-origin behaviour by passing an absolute URL
 * (the original fetch used `${config.BACKEND_URL}/api/v1/...`). axios uses
 * absolute URLs as-is, ignoring the instance baseURL.
 */
export async function saveManualItemDefinition(sessionId, content) {
  const baseUrl = config.BACKEND_URL;
  return client.post(
    `${baseUrl}/api/v1/session/${sessionId}/manual-item-definition`,
    { item_definition_markdown: content }
  );
}
